import { emptyStringToNull } from '@openx/components-form/emptyStringToNullable';
import { FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL } from '@openx/components-form/validationRegexes';
import { createAtLeastOneItemInMapSchema } from '@openx/components-form/validationSchema/atLeastOneItemInMapSchema';
import type { Nullable } from '@openx/types/helpers';
import type { TFunction } from 'i18next';
import type { Path } from 'react-hook-form';
import { number, object, string } from 'yup';
import { type CuratedDealBuyerDiscountsFormData, curatedDealBuyerDiscountApplyTo } from './types';

const createDiscountSchema = (name: string, t: TFunction) =>
  number()
    .transform(emptyStringToNull)
    .typeError(t(`{name} must be a number`, { name }))
    .min(0.1, t(`{name} must be greater than or equal to 0.1%`, { name }))
    .max(99.9, t(`{name} must be less than or equal to 99.9%`, { name }))
    .test({
      message: t(`{name} may contain only 1 digit after decimal point`, { name }),
      name: 'maxDecimalPrecision',
      test: value => FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL.test(value?.toString() || '0'),
    })
    .nullable();

export const getCuratedDealBuyerDiscountsValidationSchema = (t: TFunction) =>
  object({
    apply_to: string().nullable().required(t('Inventory is required to configure a Discount')),
    banner_and_video_discount: createDiscountSchema(t('Banner and Video Discount'), t),
    ctv_discount: createDiscountSchema(t('CTV Discount'), t),
    demand_partner: object()
      .when('apply_to' satisfies Path<CuratedDealBuyerDiscountsFormData>, {
        is: curatedDealBuyerDiscountApplyTo.DemandPartner,
        then: schema => schema.required(t('DSP is required')),
      })
      .nullable(),
    min_one_discount_error: string().test({
      exclusive: true,
      message: t('At least one Banner and Video Discount or CTV Discount is required'),
      name: 'hasAtLeastOneDiscount',
      test: (_, ctx) => {
        const formData: Nullable<CuratedDealBuyerDiscountsFormData> = ctx.parent;

        const isBannerAndVideoNumber = typeof formData.banner_and_video_discount === 'number';
        const isBannerAndVideoEmpty = !formData.banner_and_video_discount;
        const isCtvNumber = typeof formData.ctv_discount === 'number';
        const isCtvEmpty = !formData.ctv_discount;

        return isBannerAndVideoNumber || isCtvNumber || !isBannerAndVideoEmpty || !isCtvEmpty;
      },
    }),
    name: string().required(t('Discount Name is required')),
    seat_ids: object()
      .when('apply_to' satisfies Path<CuratedDealBuyerDiscountsFormData>, {
        is: curatedDealBuyerDiscountApplyTo.Seats,
        then: schema => createAtLeastOneItemInMapSchema({ label: t('Seat'), schema, t }),
      })
      .nullable(),
  } satisfies Partial<Record<keyof CuratedDealBuyerDiscountsFormData, unknown>>);
